import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutData, teamMembers } from '@/mocks/about';

const aboutPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于星空传媒',
  description: aboutData.brandIntro,
  url: 'https://liveorbit.github.io/about',
  mainEntity: {
    '@type': 'Organization',
    name: '星空传媒',
    description: aboutData.brandIntro,
    email: aboutData.contact.email,
    telephone: aboutData.contact.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '北京市朝阳区建国路88号星空大厦18层',
      addressLocality: '北京',
      addressCountry: 'CN',
    },
    sameAs: [
      'https://weibo.com/xingkongmedia',
      'https://space.bilibili.com/xingkongmedia',
    ],
  },
};

export default function AboutPage() {
  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutPageSchema)}</script>
      <SEO
        title="关于星空传媒 — 品牌介绍、口碑评价与联系方式"
        description="了解星空传媒的品牌故事、发展历程与行业口碑。星空传媒是国内领先的影视内容制作与发行机构，旗下实力派演员香菱作品合集，查看联系方式与合作渠道。"
        keywords="星空传媒品牌,星空传媒介绍,星空传媒口碑,星空传媒联系方式,星空传媒地址"
        canonicalUrl="https://liveorbit.github.io/about"
      />

      {/* Header */}
      <div className="pt-20 pb-8 bg-background-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
          <h1 className="mt-6 font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-100">
            关于星空传媒
          </h1>
          <p className="mt-3 text-foreground-400 max-w-2xl text-sm leading-relaxed">
            {aboutData.brandIntro}
          </p>
        </div>
      </div>

      {/* Brand Story */}
      <section className="py-12 md:py-16 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-100 mb-6">
            品牌故事
          </h2>
          {aboutData.brandStory.split('\n\n').map((paragraph, idx) => {
            if (!paragraph.trim()) return null;
            return (
              <p key={idx} className="text-foreground-300 leading-relaxed mb-4 text-[15px]">
                {paragraph.trim()}
              </p>
            );
          })}
        </div>
      </section>

      {/* Achievements */}
      <section className="py-12 md:py-16 bg-background-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-100 mb-8 text-center">
            荣誉与成就
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {aboutData.achievements.map((a, idx) => (
              <div
                key={idx}
                className="p-5 rounded-xl bg-background-50 border border-background-200/70 flex gap-4 items-start"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100/40 shrink-0">
                  <i className="ri-award-line text-accent-500 text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-primary-400 font-medium">{a.year}</p>
                  <p className="mt-0.5 text-sm font-semibold text-foreground-200">{a.title}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">{a.org}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-12 md:py-16 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-100 mb-8 text-center">
            核心团队
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {teamMembers.map((member, idx) => (
              <div key={idx} className="text-center">
                <div className="w-20 h-20 md:w-24 md:h-24 mx-auto rounded-full overflow-hidden border-2 border-background-200">
                  <img
                    src={member.avatar}
                    alt={member.name}
                    className="w-full h-full object-cover"
                    title={`${member.name} - ${member.role}`}
                  />
                </div>
                <p className="mt-3 text-sm font-semibold text-foreground-200">{member.name}</p>
                <p className="text-xs text-foreground-500">{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 md:py-16 bg-background-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-100 mb-8 text-center">
            观众口碑
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {aboutData.testimonials.map((t, idx) => (
              <div
                key={idx}
                className="p-5 rounded-xl bg-background-50 border border-background-200/70"
              >
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-primary-400 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-300 leading-relaxed">{t.content}</p>
                <p className="mt-3 text-xs text-foreground-500 font-medium">— {t.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 md:py-16 bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-100 mb-8 text-center">
            常见问题
          </h2>
          <div className="flex flex-col gap-3">
            {aboutData.faqs.map((faq, idx) => (
              <details key={idx} className="group rounded-xl bg-background-100 border border-background-200/70 overflow-hidden">
                <summary className="flex items-center justify-between p-4 cursor-pointer list-none">
                  <span className="text-sm font-medium text-foreground-200 pr-4">{faq.question}</span>
                  <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform shrink-0"></i>
                </summary>
                <div className="px-4 pb-4">
                  <p className="text-sm text-foreground-400 leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: aboutData.faqs.map((f) => ({
                  '@type': 'Question',
                  name: f.question,
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: f.answer,
                  },
                })),
              }),
            }}
          />
        </div>
      </section>

      {/* Contact */}
      <section className="py-12 md:py-16 bg-background-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-100 mb-8 text-center">
            联系我们
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <div className="p-5 rounded-xl bg-background-50 border border-background-200/70 text-center">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-primary-100/50">
                <i className="ri-mail-line text-primary-500 text-lg"></i>
              </div>
              <p className="mt-3 text-sm font-medium text-foreground-200">电子邮箱</p>
              <a
                href={`mailto:${aboutData.contact.email}`}
                className="mt-1 text-sm text-primary-400 hover:text-primary-300 transition-colors"
              >
                {aboutData.contact.email}
              </a>
            </div>

            <div className="p-5 rounded-xl bg-background-50 border border-background-200/70 text-center">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-primary-100/50">
                <i className="ri-phone-line text-primary-500 text-lg"></i>
              </div>
              <p className="mt-3 text-sm font-medium text-foreground-200">联系电话</p>
              <a
                href={`tel:${aboutData.contact.phone}`}
                className="mt-1 text-sm text-primary-400 hover:text-primary-300 transition-colors"
              >
                {aboutData.contact.phone}
              </a>
            </div>

            <div className="p-5 rounded-xl bg-background-50 border border-background-200/70 text-center">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-primary-100/50">
                <i className="ri-map-pin-line text-primary-500 text-lg"></i>
              </div>
              <p className="mt-3 text-sm font-medium text-foreground-200">公司地址</p>
              <p className="mt-1 text-sm text-foreground-400">{aboutData.contact.address}</p>
            </div>

            <div className="p-5 rounded-xl bg-background-50 border border-background-200/70 text-center">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-primary-100/50">
                <i className="ri-wechat-line text-primary-500 text-lg"></i>
              </div>
              <p className="mt-3 text-sm font-medium text-foreground-200">微信公众号</p>
              <p className="mt-1 text-sm text-foreground-400">{aboutData.contact.wechat}</p>
            </div>

            <div className="p-5 rounded-xl bg-background-50 border border-background-200/70 text-center">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-primary-100/50">
                <i className="ri-weibo-line text-primary-500 text-lg"></i>
              </div>
              <p className="mt-3 text-sm font-medium text-foreground-200">官方微博</p>
              <p className="mt-1 text-sm text-foreground-400">{aboutData.contact.weibo}</p>
            </div>

            <div className="p-5 rounded-xl bg-background-50 border border-background-200/70 text-center">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-primary-100/50">
                <i className="ri-time-line text-primary-500 text-lg"></i>
              </div>
              <p className="mt-3 text-sm font-medium text-foreground-200">工作时间</p>
              <p className="mt-1 text-sm text-foreground-400">{aboutData.contact.businessHours}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Back to home */}
      <section className="py-10 text-center bg-background-50">
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i> 回到首页浏览作品
        </Link>
      </section>
    </>
  );
}